import { CaretDownOutlined } from "@ant-design/icons"
import { Avatar, Button, Popover, TableColumnType, Tag } from "antd"
import React from "react"
import { PurchaseOrderIcon } from "~Components/icons/PurchaseOrderIcon"
import { UserImage } from "~Components/UserImage"
import { DeliveryNoteOptionButtons } from "~Features/pages/deliveryNotes/components/DeliveryNoteOptionButtons"
import { DeliveryNoteTableColumn } from "~Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { GenerateColumnsProps } from "~Features/pages/purchaseOrders/types/utils/helper.util"
import { useEmployees } from "~Shared/queries/employee.queries"
import { formatDate } from "~Shared/utils/date.utils"
import { TruckIcon } from "../icons/TruckIcon"

export const enum DeliveryNoteColumns {
    NAME = "name",
    VENDOR = "vendor",
    DATE = "dateOfIssue",
    CREATED_BY = "createdBy",
    LINKED_TO = "linkedTo",
    ACTION = "action",
}

export const GenerateDeliveryNoteColumns = ({
    updateRecord,
    deleteRecord,
    getRecord,
    t,
}: GenerateColumnsProps<DeliveryNoteTableColumn>): Array<TableColumnType<DeliveryNoteTableColumn>> => {
    const { data: employees = [] } = useEmployees({ excludeDeleted: true })

    return [
        {
            title: t("deliveryNote:columns.name"),
            dataIndex: DeliveryNoteColumns.NAME,
            key: DeliveryNoteColumns.NAME,
            fixed: "left" as const,
            width: 250,
            sorter: true,
            render: (name: string) => (
                <div className="flex items-center gap-8 overflow-hidden">
                    <Avatar
                        src={"#"}
                        icon={<TruckIcon />}
                        size={38}
                        style={{
                            background: "rgba(111, 186, 255, 0.10)",
                        }}
                        className="flex-shrink-0 bg-[rgba(111,186,255,0.10)] flex rounded-full border-none justify-center items-center"
                    />
                    <div className="font-bold">{name}</div>
                </div>
            ),
        },
        {
            title: t("deliveryNote:columns.vendor"),
            dataIndex: DeliveryNoteColumns.VENDOR,
            key: DeliveryNoteColumns.VENDOR,
            width: 150,
            sorter: true,
        },
        {
            title: t("deliveryNote:columns.dateOfIssue"),
            dataIndex: DeliveryNoteColumns.DATE,
            key: DeliveryNoteColumns.DATE,
            width: 150,
            sorter: true,
            render: (date: Date) => <span className="font-medium text-15">{formatDate(date)}</span>,
        },
        {
            title: t("deliveryNote:columns.createdBy"),
            dataIndex: DeliveryNoteColumns.CREATED_BY,
            key: DeliveryNoteColumns.CREATED_BY,
            width: 150,
            sorter: true,
            render: (_: unknown, record: DeliveryNoteTableColumn) => {
                const createdByUser = employees.find((item) => item.id === record.createdBy._id)
                return (
                    <div className="flex items-center gap-8 overflow-hidden">
                        {createdByUser && <UserImage user={createdByUser} />}
                        {record.createdBy.firstName} {record.createdBy.lastName}
                    </div>
                )
            },
        },
        {
            title: t("deliveryNote:columns.linkedTo"),
            dataIndex: DeliveryNoteColumns.LINKED_TO,
            key: DeliveryNoteColumns.LINKED_TO,
            width: 150,
            render: (linkedTo: Array<string>) => {
                if (!linkedTo.length) {
                    return "-"
                }

                const poTags = linkedTo.map((item, idx) => (
                    <div key={idx}>
                        <Tag className="max-w-full" color="blue">
                            <div className="flex gap-2 items-center">
                                <PurchaseOrderIcon size={24} />
                                <span className="truncate text-black">{item}</span>
                            </div>
                        </Tag>
                    </div>
                ))

                return (
                    <Popover placement="bottom" content={<div className="flex flex-col gap-2">{poTags}</div>}>
                        {linkedTo.length > 2 ? (
                            <Tag>
                                <div className="flex gap-2 items-center color-black">
                                    <PurchaseOrderIcon size={24} />
                                    <span className="text-black">
                                        {linkedTo.length} {t("label:purchase_orders")}
                                    </span>
                                </div>
                            </Tag>
                        ) : (
                            poTags
                        )}
                    </Popover>
                )
            },
        },
        {
            title: t("deliveryNote:columns.action"),
            key: DeliveryNoteColumns.ACTION,
            width: 120,
            render: (_: unknown, record: DeliveryNoteTableColumn) => (
                <DeliveryNoteOptionButtons record={record} onEdit={(rec) => updateRecord(rec)} onDelete={(rec) => deleteRecord(rec.key)} onView={(rec) => getRecord(rec)}>
                    <Button className="ant-btn-primary" onClick={(e) => e.stopPropagation()}>
                        {t("label:options")}
                        <CaretDownOutlined className="text-sm" />
                    </Button>
                </DeliveryNoteOptionButtons>
            ),
        },
    ]
}
